import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { GoogleMap } from '@angular/google-maps';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ProductService, SubscriptionService } from '@suvo-bi-users';
import { DataSourceItemsService } from '../../../../../data-source/data-source-items/services/data-source-items.service';
import { Filters } from '../../../../../filters/models/filters.model';
import { FiltersChannel } from '../../../../../filters/models/filters-channel.model';
import { IMapTileDefinition } from '../../../../interfaces/tiles/definitions/map-tile-definition.interface';
import { ITileComponent } from '../../tile-component.interface';
import { ClusterClickPreviewDialogComponent } from '../cluster-click-preview-dialog/cluster-click-preview-dialog.component';

@Component({
  standalone: false,
  selector: 'suvo-bi-map-tile',
  templateUrl: './map-tile.component.html',
  styleUrls: ['./map-tile.component.scss'],
})
export class MapTileComponent implements ITileComponent, OnInit, OnDestroy {
  @Input() tileInstance: IMapTileDefinition;
  @Input() filtersChannel: FiltersChannel;

  @ViewChild(GoogleMap) googleMap: GoogleMap;

  markers = [];
  clusterImage =
    'https://raw.githubusercontent.com/googlemaps/v3-utility-library/master/markerclustererplus/images/m';
  mapOptions: google.maps.MapOptions = null;
  loading = true;

  isTrialing = false;
  hasRestrictions = false;

  private filters: Filters;
  private readonly unsubscribe$ = new Subject<void>();

  constructor(
    private dataSourceItemsService: DataSourceItemsService,
    private readonly subscriptionService: SubscriptionService,
    private readonly productService: ProductService,
    private router: Router,
    private readonly dialog: MatDialog,
  ) {}

  ngOnInit() {
    if (this.filtersChannel) {
      this.filtersChannel?.filters
        .pipe(
          takeUntil(this.unsubscribe$),
          filter((filters) => !!filters),
        )
        .subscribe((filters) => {
          this.filters = filters;
          this.setMapTileGeolocations();
        });
    } else {
      this.setMapTileGeolocations();
    }

    this.mapOptions = {
      scrollwheel: this.tileInstance.mapOptions.canScroll,
      minZoom: 2,
      maxZoom: 15,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
    };

    this.getSubscriptionStatus();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private getSubscriptionStatus() {
    this.subscriptionService.currentSubscription
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((currentSubscription) => {
        if (currentSubscription && currentSubscription?.status === 'trialing') {
          this.isTrialing = true;
        }
        this.hasRestrictions = this.productService.hasRestrictions('GEOLOCATION', [
          'DATASOURCE:' + this.tileInstance.recipe?.dataSource,
        ]);
      });
  }

  async setMapTileGeolocations() {
    this.loading = true;

    const compositeRecipe = { ...this.tileInstance.recipe };
    if (this.filters) {
      compositeRecipe.filters = this.filters;
    }

    const geolocations = (await this.dataSourceItemsService.getGeolocated(
      compositeRecipe,
    )) as any[];
    const newBounds = new google.maps.LatLngBounds();
    const markers: any[] = geolocations
      .map((geolocation) => {
        let c = geolocation.data?.city?.data?.coords;

        if (!c) {
          c = geolocation.data?.country?.data?.coords;
        }

        if (!c) {
          return undefined;
        }
        newBounds.extend(
          new google.maps.LatLng({
            lat: c[0],
            lng: c[1],
          }),
        );
        return {
          position: {
            lat: c[0],
            lng: c[1],
          },
          // position: c,
          label: {
            color: 'white',
            text: geolocation.name,
            dataSourceItemId: geolocation._id,
          },
          dataSourceItemId: geolocation._id,
          title: geolocation._id,
          info: 'Info',
          options: {},
        };
      })
      .filter((e) => !!e);
    this.markers = markers;
    this.loading = false;

    setTimeout(() => {
      if (this.markers.length) {
        this.googleMap?.fitBounds(newBounds);
      } else {
        this.googleMap?.panTo(new google.maps.LatLng({ lat: 48.9137228, lng: -15.4660533 }));
        this.mapOptions.zoom = 3.75;
      }
    }, 0);
  }

  onMarkerClick(marker: any) {
    this.goToItem(marker.dataSourceItemId);
  }

  goToItem(itemId) {
    let prefixes = ['/', 'private', 'item', this.tileInstance.recipe?.dataSource];
    this.router.navigate([...prefixes, itemId]);
  }

  onClusterClick(cluster) {
    const dataSourceIds = cluster.markers_.map((marker) => marker.title);
    if (this.tileInstance?.recipe?.dataSource && this.googleMap.getZoom() === 15) {
      const fetchDataPromise = this.dataSourceItemsService.getPaginated(
        this.tileInstance?.recipe?.dataSource,
        null,
        null,
        null,
        dataSourceIds,
      );

      this.dialog.open(ClusterClickPreviewDialogComponent, {
        width: '500px',
        maxHeight: '500px',
        data: {
          fetchDataPromise,
          alias: this.tileInstance?.recipe?.dataSource,
          prefixes: ['/', 'private', 'item', this.tileInstance.recipe?.dataSource],
        },
      });
    }
  }
}
